<template>
  <div>
    <!-- Retraction banner — sits ABOVE the title so the user can't miss it
         before forming an impression of the work. Only shown when the
         publisher's retraction flag is set on a work. OpenAlex doesn't carry
         retraction date/reason yet (the field is a bare boolean), so the
         banner just states the fact. -->
    <v-alert
      v-if="myEntityType === 'works' && entityData?.is_retracted"
      type="error"
      variant="tonal"
      density="compact"
      icon="mdi-alert-octagon"
      class="mb-3"
    >
      This work has been retracted.
    </v-alert>

    <!-- Page-level actions, one hierarchy everywhere (#1507): the primary action
         is a labeled "Add to collection" button; everything else (Export as CSV,
         View in API, and in the fly-in, Close panel) lives in one "More actions"
         menu right after it. Status about this entity (claimed, in your
         collections) sits with the type indicator on the left, not among the
         actions. -->

    <!-- Drawer layout only: a single control bar (Notion-style). The host
         (EntityDrawer) injects close + expand on the left via `leading-controls`;
         the primary action + More menu sit on the right. #641 -->
    <div v-if="isDrawer" class="d-flex align-center drawer-controlbar mb-3">
      <slot name="leading-controls" />
      <v-spacer />
      <template v-if="!isCollection">
        <entity-collection-button
          v-if="entityData?.id && isNativeCollectionType"
          :entity-type="myEntityType"
          :entity-id="entityData.id"
          :z-index="10001"
          class="mr-1"
        />
        <entity-more-menu
          :api-url="apiUrl"
          :show-export="canExport"
          show-close-item
          :z-index="10001"
          @export="onExport"
          @close="$emit('close')"
        />
      </template>
    </div>

    <!-- Row 0: back button (if the user came from a SERP), entity-type indicator
         and status (claimed badge, your-collection chips) on the left; on the
         full page, the actions on the far right. In the drawer this row is the
         eyebrow: type + status only (the actions live in the control bar). -->
    <div class="d-flex align-center flex-wrap header-meta-row mb-2">
      <v-btn
        v-if="!isDrawer && showBackButton && cameFromSerp"
        color="primary"
        size="small"
        density="compact"
        variant="text"
        icon
        aria-label="Back"
        class="back-btn mr-2"
        @click="onBackClick"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Collections reuse this header (isCollection) to match the entity-page
           look, but a collection isn't an OpenAlex entity: the type indicator
           becomes a folder + "Collection", and the entity-only affordances
           (claim, collection chips, entity actions) are suppressed. -->
      <div v-if="isCollection" class="entity-type-indicator">
        <v-icon size="x-small" variant="plain">mdi-folder-outline</v-icon>
        {{ typeLabel }}
      </div>
      <link-entity-roles-list
        v-else-if="entityData.roles"
        :roles="entityData.roles"
        :selected="myEntityConfig.nameSingular"
      />
      <div v-else class="entity-type-indicator">
        <v-icon size="x-small" variant="plain">{{ myEntityConfig.icon }}</v-icon>
        {{ filters.capitalize(myEntityConfig.displayNameSingular) }}
      </div>

      <!-- Status that belongs next to the type, not among the actions (#1508:
           a collection's Private / Shared by link). -->
      <slot name="meta-status" />
      <template v-if="!isCollection && entityData?.id">
        <entity-header-claim-profile-button
          v-if="myEntityType === 'authors'"
          :author-id="shortId"
          class="ml-2"
        />
        <entity-collections-row
          :entity-type="myEntityType"
          :entity-id="entityData.id"
          compact
          class="ml-3"
        />
      </template>

      <template v-if="!isDrawer">
        <v-spacer />
        <div class="header-actions d-flex align-center">
          <template v-if="!isCollection">
            <entity-collection-button
              v-if="entityData?.id && isNativeCollectionType"
              :entity-type="myEntityType"
              :entity-id="entityData.id"
              class="mr-1"
            />
            <entity-more-menu
              :api-url="apiUrl"
              :show-export="canExport"
              @export="onExport"
            />
          </template>
          <slot name="header-actions" />
        </div>
      </template>
    </div>

    <!-- Export as CSV: the search page's export dialog, scoped to this entity's
         works. Its own activator stays hidden; the More menu opens it. -->
    <serp-results-export-button
      v-if="canExport"
      ref="exportButtonRef"
      :scope="{ filter: worksExportFilter, count: exportCount }"
      :z-index="isDrawer ? 10001 : null"
      class="d-none"
    />

    <!-- Row 1: title. -->
    <div class="d-flex align-start">
      <!-- A collection's name is user text, and a collection shared by link shows it
           to other people: render it as plain text, never HTML (oxjob #646). -->
      <div v-if="isCollection" :class="titleClass">{{ displayTitle }}</div>
      <!-- #1689: the record's name is the page's one <h1> on the full page;
           the drawer sits on a search page, so it stays a div there. -->
      <div
        v-else-if="isDrawer"
        :class="titleClass"
        v-html="filters.prettyTitle(displayTitle)"
      />
      <h1
        v-else
        :class="[titleClass, 'entity-title']"
        v-html="filters.prettyTitle(displayTitle)"
      />
      <slot name="after-title" />
      <v-spacer />
    </div>
    <slot name="after-header" />

    <!-- Row 2 (works/locations only): linkouts. -->
    <div
      v-if="myEntityType === 'works' || myEntityType === 'locations'"
      class="d-flex align-center flex-wrap mt-3"
    >
      <work-linkouts v-if="myEntityType === 'works'" :data="entityData"/>
      <location-linkouts v-else :data="entityData"/>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

import filters from '@/filters';
import { getEntityConfig } from '@/entityConfigs';
import { createSimpleFilter, filtersAsUrlStr } from '@/filterConfigs';
import { api } from '@/api';
import * as openalexId from '@/openalexId';

import LinkEntityRolesList from '@/components/LinkEntityRolesList.vue';
import WorkLinkouts from '@/components/WorkLinkouts.vue';
import LocationLinkouts from '@/components/LocationLinkouts.vue';
import EntityHeaderClaimProfileButton from '@/components/Entity/EntityHeaderClaimProfileButton.vue';
import EntityCollectionsRow from '@/components/Collection/EntityCollectionsRow.vue';
import EntityCollectionButton from '@/components/Entity/EntityCollectionButton.vue';
import EntityMoreMenu from '@/components/Entity/EntityMoreMenu.vue';
import SerpResultsExportButton from '@/components/SerpResultsExportButton.vue';

defineOptions({ name: 'EntityHeader' });

const props = defineProps({
  entityData: Object,
  entityType: String,
  // Render the back button in the header-meta row when the user came from a
  // SERP-like page. Set false in surfaces that already have their own close
  // affordance (e.g. EntityDrawer).
  showBackButton: { type: Boolean, default: true },
  // Collection mode: the header represents a user collection, not an OpenAlex
  // entity. Swaps the type indicator for a folder + "Collection" and hides the
  // entity-only affordances (claim, collection chips, API link, collection
  // menu). entityData is the collection object (display_name, id `col_…`).
  isCollection: { type: Boolean, default: false },
  // Optional override for the collection-mode type indicator text. Lets the
  // collection page say "Collection of institutions" (oxjob #366) instead of the
  // bare "Collection". Ignored unless isCollection.
  typeLabel: { type: String, default: "Collection" },
  // Layout variant. 'full' (default) = the standard entity-page header.
  // 'drawer' = the fly-in variant: a single Notion-style control bar (host
  // injects close/expand via #leading-controls; Add to collection + More on the right) with
  // the type indicator + chips demoted to an eyebrow row. #641.
  layout: { type: String, default: "full" },
});

// Emitted only in drawer layout, from the More menu's "Close panel" item.
defineEmits(["close"]);

const isDrawer = computed(() => props.layout === "drawer");

// Export as CSV (#1507): exports this entity's works through the search page's
// export dialog (columns, cost, emailed when ready). On a work page the filter
// is the work's own id, so it exports that one work with the same columns.
const exportButtonRef = ref(null);
const worksExportFilter = computed(() => {
  if (props.isCollection || !shortId.value) return null;
  const config = myEntityConfig.value;
  if (!config?.filterKey) return null;
  // Repository sources: works where the repo is any location, as the page's
  // works list does.
  const isRepository = myEntityType.value === 'sources' && props.entityData?.type === 'repository';
  const filterKey = isRepository ? 'locations.source.id' : config.filterKey;
  return filtersAsUrlStr([createSimpleFilter('works', filterKey, shortId.value)]);
});
const canExport = computed(() => !!worksExportFilter.value);
const exportCount = ref(0);
async function onExport() {
  try {
    const resp = await api.getResultsList(
      api.makeUrl('works', { filter: worksExportFilter.value, 'per-page': 1 }, true)
    );
    exportCount.value = resp?.meta?.count ?? 0;
  } catch (e) {
    exportCount.value = 0;
  }
  exportButtonRef.value?.openExportDialog();
}

const store = useStore();
const router = useRouter();

const id = computed(() => props.entityData?.id);
const shortId = computed(() => openalexId.getShortId(id.value));
const normalizedId = computed(() => openalexId.normalizeId(id.value));
const isNative = computed(() => openalexId.isNativeEntityType(myEntityType.value));
const myEntityType = computed(() => props.entityType || openalexId.getEntityType(id.value));
const myEntityConfig = computed(() => getEntityConfig(myEntityType.value));

// Collections feature: the Add to collection button is gated on the entity
// being a collectible type (EntityCollectionsRow handles its own auth gate; the
// button sends logged-out users to log in). #394 widened collections to every
// users-api SUPPORTED_ENTITY_TYPES; #396 re-enabled work-types by mapping the
// GUI page type through toCollectionEntityType (`types` → `work-types`) before
// gating (openalexId.isCollectibleEntityType).
const isNativeCollectionType = computed(() =>
  openalexId.isCollectibleEntityType(myEntityType.value)
);

// Read once on mount — history.state.back reflects the previous in-app
// navigation, and stays stable while we're on this page. Direct hits (typing
// a URL, opening from an external link) have no `.back`, so the button stays
// hidden.
const cameFromSerp = ref(false);
const SERP_PATH_RE = /^\/(works|authors|sources|institutions|topics|sdgs|funders|publishers|keywords|concepts|collections|locations)(\/|\?|$)/;
onMounted(() => {
  const prev = window.history.state?.back || '';
  cameFromSerp.value = SERP_PATH_RE.test(prev);
});

function onBackClick() {
  router.back();
}

const titleClass = computed(() => {
  const base = 'font-weight-bold mb-2';
  return myEntityType.value === 'works'
    ? `text-h5 text-lg-h4 ${base}`
    : `text-h4 text-lg-h3 ${base}`;
});

// For locations, use the title field as the display name and append source name
// For awards, use funder_award_id fallback if no display_name
const displayTitle = computed(() => {
  if (myEntityType.value === 'locations') {
    const title = props.entityData?.title || props.entityData?.id || 'Location';
    const sourceName = props.entityData?.source_name || props.entityData?.source?.display_name;
    return sourceName ? `${title} [${sourceName}]` : title;
  }
  if (myEntityType.value === 'awards') {
    return filters.getAwardDisplayTitle(props.entityData);
  }
  return props.entityData?.display_name;
});

// Compute the API URL with data-version parameter if needed
const apiUrl = computed(() => {
  // For locations, use the full entity ID path
  let path;
  if (myEntityType.value === 'locations') {
    path = 'locations/' + props.entityData?.id;
  } else if (isNative.value) {
    path = shortId.value;
  } else {
    path = normalizedId.value;
  }
  const baseUrl = 'https://api.openalex.org/' + path;
  if (store.state.useV2) {
    return baseUrl + '?data-version=2';
  }
  return baseUrl;
});

</script>



<style scoped lang="scss">
h1.entity-title {
  // Vuetify's text-h* classes set the type; drop the browser's default h1 margin-top.
  margin-top: 0;
}
.header-meta-row {
  // Subtle visual weight for the meta strip: type indicator + collection chips
  // shouldn't outshout the title beneath.
  color: rgba(0, 0, 0, 0.72);
  font-size: 0.95rem;
}
.entity-type-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.back-btn {
  // Tighten so the back chevron hugs the left edge of the meta row.
  margin-left: -8px;
}
</style>
